import { noop } from 'lodash-es'
import { DefaultLastWorkspaceStorage } from '@/features/iam/workspaces/LastWorkspaceStorage'
import type { LegendsFeatureOptions } from '@/features/legends'
import { LegendBuilderApi } from '@/features/legends/api/legends-api'
import { LegendEditorViewModel } from '@/features/legends/LegendEditor/LegendEditorViewModel'
import { LegendListViewModel } from '@/features/legends/LegendList/LegendListViewModel'
import { LegendStore } from '@/features/legends/LegendStore'
import { WorkspaceSelectorViewModel } from '@/features/legends/WorkspaceSelectorViewModel'
import { env } from '@/infra/env'
import type { Id } from '@/infra/id'
import { localStorage } from '@/infra/storage'
import { waitForViewTransition } from '@/utils/browser-util'
import { memoize } from '@/utils/memoize-util'

/**
 * Function to acquire the view model for a legend's editor.
 */
export type AcquireLegendEditorViewModelFn = (
  legendId: Id,
  signal?: AbortSignal,
) => Promise<LegendEditorViewModel>

export function InternalLegendsFeature(options: LegendsFeatureOptions) {
  const legendStore = new LegendStore(
    new LegendBuilderApi({ baseURL: env.LEGEND_API, token$: options.token$ }),
    options.primeMember,
  )

  const legendListViewModel = new LegendListViewModel(
    new WorkspaceSelectorViewModel(
      options.getCurrentUser,
      options.getCurrentUserWorkspaceMemberships,
      options.getWorkspaceSelection,
      new DefaultLastWorkspaceStorage(localStorage, 'legends'),
      localStorage,
    ),
    legendStore,
    options.navigate,
    options.createAlertErrorHandler,
  )

  // The editor needs the jobs feature's question list, so we start loading it
  // now and opening a legend doesn't wait for it. If it fails, opening the
  // editor fails too.
  options.createQuestionListViewModel.then(noop, noop)

  const acquireLegendEditorViewModel: AcquireLegendEditorViewModelFn = memoize(
    async (legendId: Id, signal?: AbortSignal) => {
      const createQuestionListViewModel =
        await options.createQuestionListViewModel
      signal?.throwIfAborted()

      return new LegendEditorViewModel({
        legendId,
        legendStore,
        storage: localStorage,
        navigate: options.navigate,
        newAlert: options.newAlert,
        createAlertErrorHandler: options.createAlertErrorHandler,
        createAlertRetryWrapper: options.createAlertRetryWrapper,
        getCurrentUser: options.getCurrentUser,
        getCurrentUserWorkspaceMemberships:
          options.getCurrentUserWorkspaceMemberships,
        listRoles: options.listRoles,
        getWorkspaceRoles: options.getWorkspaceRoles,
        listSkills: options.listSkills,
        getWorkspaceSkills: options.getWorkspaceSkills,
        listInventoryItems: options.listInventoryItems,
        fetchInventoryItemExports: options.fetchInventoryItemExports,
        importInventoryItems: options.importInventoryItems,
        listBundles: options.listBundles,
        createQuestionListViewModel,
        waitForViewTransition,
      })
    },
    {
      // Only the open legend's editor is kept, so a revalidating route reuses
      // it while opening another legend starts afresh.
      maxCount: 1,
      promise: ([, signal]) => signal,
    },
  )

  return {
    // Internal
    legendListViewModel,
    acquireLegendEditorViewModel,
  }
}
